import { createReportTransport, reportPath } from 'shared/api/reportTransport';
import { authenticatedFetch, SESSION_CHANGED } from 'shared/api/session';
import { SOURCE_IDENTITY_CHANGED } from 'shared/api/sourceIdentity';
import { consumeJsonLines } from 'shared/api/jsonLines';
import { followReadJob, ReadJob } from 'shared/api/readJobs';
import { recoverSourceAuth, sourceAuthPayload } from 'shared/api/sourceAuthRecovery';
import { config } from 'shared/config';
import { CanceledError } from 'axios';
import { MODEL_CHANGED_EVENT, modelChangeAffectsData } from 'features/semantic-model/public/definitionChanges';
import { LiveAnswer, Report, Results, Interaction, QueryWindow, Result } from './types';
import { stableJSON } from 'shared/lib/stableJson';
import type { SaveRequest } from './pageSave';
import { isHtmlLink, linkReport, readLinkChanges, readLinkData } from './public/linkReads';
export { reportPath, errorText } from 'shared/api/reportTransport';

export const { api, client: htmlClient } = createReportTransport('/api/html');
// The page layer's routes (live viewing, leases), the same for both kinds of page.
const { api: pageLayer } = createReportTransport('/api/pages');
/** `/api/html/pages/<uid>`: one HTML page's projection and writes (its live feed is the page layer's `/api/pages/<uid>`). */
export const pagePath = (pageUid: string) => '/pages/' + pageUid;
/** The net change of a page since the version a viewer shows (View access): in-place patches, or a request to rebuild the frame. */
export const readLiveChanges = (pageUid: string, after: number, signal?: AbortSignal): Promise<LiveAnswer> =>
  isHtmlLink(pageUid) ? readLinkChanges(after, signal)
    : pageLayer.get<LiveAnswer>('/' + pageUid + '/live?after=' + after, signal ? {signal} : undefined);
export const readHtmlPage = (pageUid: string, options?: {signal?: AbortSignal}): Promise<Report> =>
  isHtmlLink(pageUid) ? Promise.resolve(linkReport()) : api.get<Report>(pagePath(pageUid), options);
/**
 * Page writes carry a request id per user action. A transport failure (no answer from the server) is retried
 * once with the same id, which the server treats as the same command; an answered error is never retried.
 */
export const postPageCommand = async <T,>(pageUid: string, route: string, body: object, clientRequestId: string = crypto.randomUUID()): Promise<T> => {
  const payload = {...body, clientRequestId};
  try { return await api.post<T>(pagePath(pageUid) + route, payload); }
  catch (error: any) {
    if (error?.response) throw error;
    return api.post<T>(pagePath(pageUid) + route, payload);
  }
};
/** A page script's text saved into a save-enabled element (Edit content or above); a transport failure is retried once under the same id. */
export const savePageText = (pageUid: string, request: SaveRequest): Promise<{version: number}> =>
  postPageCommand<{version: number}>(pageUid, '/script-save', request);
/** `baseVersion` is the page version the change was made on (the server applies it on a later version when what it reads or writes is the
 * same there, and refuses it with `revision_conflict` otherwise); it defaults to the version of `report`, the page as it is shown now. */
export const changePage = (report: Report, pageUid: string, operation: object, label: string, baseVersion: number = report.version): Promise<Report> =>
  postPageCommand<Report>(pageUid, '/operations', {baseVersion, operation, label});

type Progress = {results: Results; pending: string[]};
type DataOptions = {signal?: AbortSignal; onProgress?: (progress: Progress) => void};
type CacheEntry = {promise: Promise<Results>; results?: Results; controller: AbortController; consumers: number;
  done: boolean; listeners: Set<(progress: Progress) => void>; progress?: Progress};
const cache = new Map<string, CacheEntry>();
type PageData = {context: unknown; results: Results};
type Primed<T> = {expiresAt: number; value: Promise<T>; controller: AbortController};
// An HTML page's projection and base data reads, started together (at report
// open, or when the page is opened) and taken once by the page.
const primedDocuments = new Map<string, Primed<Report>>();
const primedData = new Map<string, Primed<PageData>>();
// When each page last took its reads; a late report-open prefetch does not read it again.
const opened = new Map<string, number>();
// Last base (no interaction) cache key per page, so a revisit with cached data is not read again.
const baseKeys = new Map<string, string>();
const PRIME_TTL_MS = 15000;
const primeKey = (...parts: string[]) => parts.join(':').toLowerCase();
const clearReaderData = () => {
  cache.forEach(entry => entry.controller.abort()); cache.clear();
  [primedDocuments, primedData].forEach(primed => { primed.forEach(entry => entry.controller.abort()); primed.clear(); });
  opened.clear(); baseKeys.clear();
};
window.addEventListener(SESSION_CHANGED, clearReaderData);
window.addEventListener(SOURCE_IDENTITY_CHANGED, clearReaderData);
// A data-changing model change (relationships, definitions, sources) changes the
// results of the report's unchanged query contexts: none of its kept data is reused.
window.addEventListener(MODEL_CHANGED_EVENT, event => {
  const reportId = String((event as CustomEvent).detail?.reportId || '').toLowerCase();
  if (!reportId || !modelChangeAffectsData(event)) return;
  const prefix = stableJSON([reportId]).slice(0, -1) + ',';
  cache.forEach((_, key) => { if (key.toLowerCase().startsWith(prefix)) cache.delete(key); });
  primedData.forEach((entry, key) => { if (key.startsWith(reportId + ':')) { entry.controller.abort(); primedData.delete(key); } });
  baseKeys.forEach((_, key) => { if (key.startsWith(reportId + ':')) baseKeys.delete(key); });
});
const prime = <T,>(store: Map<string, Primed<T>>, key: string, read: (signal: AbortSignal) => Promise<T>) => {
  if ((store.get(key)?.expiresAt ?? 0) > Date.now()) return;
  const controller = new AbortController();
  const value = read(controller.signal);
  value.catch(() => undefined);
  store.set(key, {expiresAt: Date.now() + PRIME_TTL_MS, value, controller});
};
const takePrimed = <T,>(store: Map<string, Primed<T>>, key: string): Primed<T> | undefined => {
  const entry = store.get(key);
  store.delete(key);
  return entry && entry.expiresAt > Date.now() ? entry : undefined;
};
const primePage = (reportId: string, pageId: string) => {
  const key = primeKey(reportId, pageId);
  prime(primedDocuments, key, signal => readHtmlPage(pageId, {signal}));
  const known = baseKeys.get(key);
  if (known && cache.has(known)) return;
  prime(primedData, key, signal => api.get<PageData>(reportPath(reportId) + '/pages/' + pageId + '/data', {signal}));
};
/**
 * Report open: start an HTML page's document and base data reads as soon as
 * the page is known, before the page mounts. Ignored for a page that has just
 * taken its reads.
 */
export const prefetchHtmlPage = (reportId: string, pageId: string): void => {
  if (!reportId || !pageId || (opened.get(primeKey(reportId, pageId)) ?? 0) > Date.now() - PRIME_TTL_MS) return;
  primePage(reportId, pageId);
};
/**
 * The page's projection read. Its base data read starts at the same time
 * instead of after it; loadData uses that result only when it carries the
 * exact query context of the document the page shows, else it reads again.
 */
export const openHtmlPage = (reportId: string, pageId: string): Promise<Report> => {
  if (isHtmlLink(reportId)) return Promise.resolve(linkReport());
  const key = primeKey(reportId, pageId);
  opened.set(key, Date.now());
  primePage(reportId, pageId);
  return takePrimed(primedDocuments, key)?.value ?? readHtmlPage(pageId);
};
/**
 * The page's data context: the server's digest of its data definitions and the filters as they apply to this reader (a person's
 * own filter values stay on the server). Every data response repeats it; a different one means the page must be read again.
 */
export const queryContext = (report: Report, _pageId: string): string => report.dataContext ?? '';
/** The key a dataset's result is stored under, for one visual's view of it (`<visual>/<dataset>`) or for the dataset itself. */
export const resultKey = (dataId: string, visualId?: string): string => visualId ? visualId + '/' + dataId : dataId;
export const loadingResultSlots = (report: Report, pageId: string, interaction?: Interaction | null): string[] => {
  const page = report.document.pages.find(p => p.id === pageId)!;
  // Relationships belong to the report model, including outputs on other sources.
  // Only the selecting visual keeps its baseline; the server resolves applicability.
  return page.data.flatMap(d =>
    [d.id, ...page.visuals.filter(v => v.id !== interaction?.visualId && v.dataIds.includes(d.id)).map(v => resultKey(d.id, v.id))]);
};
export const dataKey = (report: Report, pageId: string, interaction?: Interaction | null): string => {
  return stableJSON([report.id, queryContext(report, pageId), interaction || null]);
};
export class DataChangedError extends Error {}
export const invalidateData = (report: Report, pageId: string) => {
  const prefix = stableJSON([report.id, queryContext(report,pageId)]).slice(0,-1) + ',';
  cache.forEach((_, key) => { if (key.startsWith(prefix)) cache.delete(key); });
};
function consume(entry: CacheEntry, key: string, options: DataOptions): Promise<Results> {
  entry.consumers++;
  return new Promise((resolve, reject) => {
    let finished = false;
    const finish = () => {
      if (finished) return false;
      finished = true; entry.consumers--;
      if (options.onProgress) entry.listeners.delete(options.onProgress);
      options.signal?.removeEventListener('abort', abort);
      return true;
    };
    const abort = () => {
      if (!finish()) return;
      reject(new CanceledError('This HTML data context was superseded.'));
      if (!entry.done && entry.consumers === 0) {
        if (cache.get(key) === entry) cache.delete(key);
        entry.controller.abort();
      }
    };
    entry.promise.then(value => {if (finish()) resolve(value);}, error => {if (finish()) reject(error);});
    if (options.signal?.aborted) {abort(); return;}
    options.signal?.addEventListener('abort', abort, {once: true});
    if (options.onProgress) {
      entry.listeners.add(options.onProgress);
      if (entry.progress) options.onProgress(entry.progress);
    }
  });
}

export const loadData = (report: Report, pageId: string, refresh = false, interaction?: Interaction | null, options: DataOptions = {}): Promise<Results> => {
  const key = dataKey(report, pageId, interaction);
  if (refresh) invalidateData(report, pageId);
  const cached = cache.get(key);
  if (cached) return consume(cached, key, options);
  // The selecting visual keeps its authored baseline.
  // Reuse only a completed, error-free response with the exact query context.
  const baseline = interaction ? cache.get(dataKey(report, pageId, null))?.results : undefined;
  const targets = baseline && interaction ? loadingResultSlots(report, pageId, interaction) : undefined;
  const entry = {controller: new AbortController(), consumers: 0, done: false, listeners: new Set()} as CacheEntry;
  const query = interaction ? '?state=' + encodeURIComponent(JSON.stringify({interaction, targets})) : '';
  const path = reportPath(report.id) + '/pages/' + pageId;
  if (!interaction) baseKeys.set(primeKey(report.id, pageId), key);
  const primed = interaction ? undefined : takePrimed(primedData, primeKey(report.id, pageId));
  if (refresh) primed?.controller.abort();
  const request = async () => {
    if (isHtmlLink(report.id)) return readLinkData({interaction, targets}, undefined, entry.controller.signal);
    if (primed && !refresh) {
      entry.controller.signal.addEventListener('abort', () => primed.controller.abort(), {once: true});
      const response = await primed.value.catch(() => undefined);
      if (response && stableJSON(response.context) === stableJSON(queryContext(report, pageId))) return response;
    }
    if (!baseline || !interaction || !options.onProgress) return api.get<PageData>(path + '/data' + query, {signal: entry.controller.signal});
    const response = await authenticatedFetch(config.API_URL + '/api/html' + path + '/data-stream' + query, {signal: entry.controller.signal});
    let context: unknown, complete = false;
    let pending = new Set<string>();
    let partial: Results = Object.fromEntries(Object.entries(baseline).filter(([slot]) => !targets!.includes(slot)));
    const followers: Promise<void>[] = [];
    const receive = (results: Results) => {
      partial = {...partial, ...results};
      Object.keys(results).forEach(slot => pending.delete(slot));
      entry.progress = {results: partial, pending: Array.from(pending)};
      entry.listeners.forEach(listener => listener(entry.progress!));
    };
    await consumeJsonLines(response, event => {
      if ('context' in event) {
        context = event.context;
        if (stableJSON(context) !== stableJSON(queryContext(report, pageId))) throw new DataChangedError('The query definitions changed while data was loading.');
        pending = new Set(event.keys);
      } else if (event.results) {
        if (context === undefined) throw new Error('The HTML data stream is missing its query context.');
        receive(event.results);
        return;
      } else if (event.jobs) {
        // Reads still running after the server's sync window continue as read jobs (DP-031).
        Object.values(event.jobs as Record<string, {keys: string[]; readJob: ReadJob}>).forEach(({keys, readJob}) => {
          followers.push(followReadJob(readJob, {send: authenticatedFetch, signal: entry.controller.signal}).then(outcome => receive(outcome.status === 200
            ? outcome.data.results
            : Object.fromEntries(keys.map(slot => [slot, {...outcome.data, error: outcome.data?.error || 'This read could not finish.'} as Result])))));
        });
        return;
      } else if (event.done) {complete = true; return;}
      entry.progress = {results: partial, pending: Array.from(pending)};
      entry.listeners.forEach(listener => listener(entry.progress!));
    });
    await Promise.all(followers);
    if (!complete || pending.size || context === undefined) throw new Error('The HTML refresh ended before every result arrived.');
    const auth = sourceAuthPayload({ results: partial });
    if (auth && await recoverSourceAuth(auth, { reportId: report.id, signal: entry.controller.signal })) {
      return api.get<PageData>(path + '/data' + query, { signal: entry.controller.signal });
    }
    return {context, results: partial};
  };
  // No overall time limit: a healthy read runs; the server stops cancelled, abandoned or stalled reads (DP-031).
  entry.promise = request()
      .then(response => {
        if (stableJSON(response.context) !== stableJSON(queryContext(report, pageId))) {
          throw new DataChangedError('The query definitions changed while data was loading.');
        }
        const results = baseline ? { ...baseline, ...response.results } : response.results;
        if (Object.values(results).some(r => r.error)) {
          if (cache.get(key) === entry) cache.delete(key);
        } else entry.results = results;
        return results;
      }).catch(error => { if (cache.get(key) === entry) cache.delete(key); throw error; })
      .finally(() => {entry.done = true;});
  cache.set(key, entry);
  if (cache.size > 40) cache.delete(cache.keys().next().value);
  return consume(entry, key, options);
};

export async function queryVisual(report: Report, pageId: string, visualId: string, dataId: string, window: QueryWindow, interaction?: Interaction | null): Promise<Result> {
  const target = resultKey(dataId, visualId);
  const response = isHtmlLink(report.id) ? await readLinkData({ interaction, window }, target)
    : await api.get<{ context: unknown; results: Results }>(reportPath(report.id) + '/pages/' + pageId +
      '/data?target=' + encodeURIComponent(target) + '&state=' + encodeURIComponent(JSON.stringify({ interaction, window })));
  if (stableJSON(response.context) !== stableJSON(queryContext(report, pageId))) throw new DataChangedError('This visual changed while its rows were loading.');
  const result = response.results[target];
  if (result?.error) throw new Error(result.error);
  if (!result) throw new Error('No data returned for this registered visual.');
  return result;
}
